import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDepositReport } from '../../hooks/useDepositReport';
import { DepositReportHeader } from './DepositReportHeader';
import { DepositReportStats } from './DepositReportStats';
import { DepositReportFilters } from './DepositReportFilters';
import { PaymentMethodTotals } from './PaymentMethodTotals';
import { UserAgentTotals } from './UserAgentTotals';
import { DepositReportTable } from './DepositReportTable';
import { DepositReportPagination } from './DepositReportPagination';
import { PrintSummaryModal } from './PrintSummaryModal';
import { useApi } from '../Store/ApiContext';

export const DepositCollectionReportPage = () => {
  const { t } = useTranslation();
  const { responseApi, protectedFunction } = useApi();


  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Use custom hook for all report logic
  const {
    // Data
    filteredRecords,
    paginatedRecords,
    paymentMethodTotals,
    userTotals,
    stats,
    uniqueUsers,

    // State
    isLoading,
    isExporting,

    // Filters
    searchTerm,
    setSearchTerm,
    userFilter,
    setUserFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    requestIdFilter,
    setRequestIdFilter,
    recordTypeFilter,
    setRecordTypeFilter,
    activeQuickFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    hasActiveFilters,

    // Sorting and pagination
    sortBy,
    sortOrder,
    currentPage,
    totalPages,
    itemsPerPage,

    // Handlers
    handleRefresh,
    handleExport,
    handleSort,
    handleQuickDateFilter,
    handleClearFilters,
    handlePageChange
  } = useDepositReport();

  const handlePrintSummary = () => {
    setShowPrintModal(true);
  };

  const filters = {
    searchTerm,
    userFilter,
    dateFrom,
    dateTo,
    requestIdFilter,
    recordTypeFilter,
    activeQuickFilter
  };

  useEffect(() => {
    // add title page
    document.title = t("depositCollectionReport");
    protectedFunction(['Front Office Agent'], () => { 
      setUserFilter(localStorage.getItem("userName"));
    });
  }, []);




  return (
    <div className={`bg-babyblue-50 big-card container-fluid`}>
      <div className="pt-4 pb-3 px-2 mx-3">
        {/* Header */}
        <DepositReportHeader
          onRefresh={handleRefresh}
          onExport={handleExport}
          onPrintSummary={handlePrintSummary}
          isLoading={isLoading}
          isExporting={isExporting}
          isPrinting={isPrinting}
        />

        {/* Stats Cards */}
        <DepositReportStats
          totalRecords={stats.totalRecords}
          totalCollected={stats.totalCollected}
          totalRefunds={stats.totalRefunds}
          netAmount={stats.netAmount}
          activeAgents={stats.activeAgents}
          averageDeposit={stats.averageDeposit}
        />

        {/* Filters */}
        <DepositReportFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          userFilter={userFilter}
          onUserFilterChange={setUserFilter}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
          requestIdFilter={requestIdFilter}
          onRequestIdFilterChange={setRequestIdFilter}
          recordTypeFilter={recordTypeFilter}
          onRecordTypeFilterChange={setRecordTypeFilter}
          showAdvancedFilters={showAdvancedFilters}
          onToggleAdvancedFilters={() => setShowAdvancedFilters(!showAdvancedFilters)}
          onClearFilters={handleClearFilters}
          onQuickDateFilter={handleQuickDateFilter}
          activeQuickFilter={activeQuickFilter}
          uniqueUsers={uniqueUsers}
          hasActiveFilters={hasActiveFilters}
          isLoading={isLoading}
        />

        {/* Payment Method Totals */}
        <PaymentMethodTotals
          methodTotals={paymentMethodTotals}
          totalAmount={stats.totalCollected}
        />

        {/* User/Agent Totals */}
        <UserAgentTotals
          userTotals={userTotals}
        />

        {/* Results Table */}
        <DepositReportTable
          records={paginatedRecords}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />

        {/* Pagination */}
        <DepositReportPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredRecords.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />

        {/* Print Summary Modal */}
        <PrintSummaryModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          records={filteredRecords}
          paymentMethodTotals={paymentMethodTotals}
          userTotals={userTotals}
          filters={filters}
          stats={stats}
        />
      </div>
    </div>
  );
};

export default DepositCollectionReportPage;